import * as PIXI from "../../src/core/PIXI";
import gsap, { Linear, Power1, Power2 } from "gsap"

export default class PlaneAndGraph
{

    app: PIXI.Application;
    stage: PIXI.Container;

    // gfx
    graphFill: PIXI.Graphics;
    graphLine: PIXI.Graphics;
    graphMask: PIXI.Graphics;
    graphContainer: PIXI.Container;
    plane: PIXI.AnimatedSprite;

    planeSpriteTicker: number = 1;
    planeSpriteId: number = 1;

    planeRelHeight: number = 20;

    pad: number = 30;
    containerCanvas: PIXI.Container;

    gameOver: boolean = true;

    graphLineThickness: number = 4;

    // settings
    animations: boolean = false;

    points: Array<PIXI.Point>;

    roundStartedAt: number;
    

    constructor(app: PIXI.Application)
    {
        this.app = app;
        this.stage = app.stage;

        this.tickerUpdate = this.tickerUpdate.bind(this);

        this.containerCanvas = new PIXI.Container();
        this.stage.addChild(this.containerCanvas);

        this.createGraphMask();
        this.createGraph();
        this.createPlane();

        this.containerCanvas.x = this.pad;
        this.containerCanvas.y = -this.pad;

        this.roundStartedAt = performance.now();

        requestAnimationFrame(this.tickerUpdate);
    }


    onToggleAnimation(animations: boolean)
    {
        this.animations = animations;
        this.containerCanvas.visible = animations;
    }


    cleanUp()
    {
        this.graphLine.scale.set(1);
        this.graphFill.scale.set(1);
        this.graphMask.width = 0;
        this.graphMask.height = 0;
    }


    readyPlane()
    {
        gsap.killTweensOf(this.plane);
        this.plane.visible = true;
        this.plane.position.set(
            this.points[0].x,
            this.points[0].y
        );

        this.cleanUp();
    }


    hidePlane()
    {
        this.plane.visible = false;
    }


    startRound()
    {
        this.roundStartedAt = performance.now();

        this.gameOver = false;
        
        this.readyPlane();
    }


    stopRound()
    {
        // plane flies away
        gsap.to(this.plane, {
            x: this.plane.x + this.app.width,
            y: this.plane.y - this.app.height * .5,
            ease: Linear.easeInOut,
            duration: 1,
        });

        this.cleanUp();

        this.gameOver = true;
    }


    createGraphMask()
    {
        this.graphContainer = new PIXI.Container();
        this.containerCanvas.addChild(this.graphContainer);

        this.graphMask = new PIXI.Graphics();
        this.graphMask.beginFill(0xffffff, .5);
        this.graphMask.drawRect(0, 0, this.app.stage.width, this.app.stage.height);
        this.graphMask.endFill();
        this.containerCanvas.addChild(this.graphMask);

        this.graphFill = new PIXI.Graphics();
        this.graphFill.mask = this.graphMask;
        this.graphContainer.addChild(this.graphFill);

        this.graphLine = new PIXI.Graphics();
        this.graphLine.mask = this.graphMask;
        this.graphContainer.addChild(this.graphLine);
    }


    createGraph()
    {
        let _h = this.app.height;
        let _w = this.app.width;
        if (this.points?.length > 0)
        {
            _h = this.app.height - this.points[1].y;
            _w = this.points[1].x;
        }

        const _thickness = this.graphLineThickness;

        this.graphFill?.clear();
        this.graphFill
            .moveTo(0, _h)
            .beginFill(0xcf1426, 0.5)
            .lineTo(0, _h)
            .quadraticCurveTo(
                _w / 2, _h,
                _w, 0,
            )
            .lineTo(_w, _h);
        this.graphFill.endFill();

        this.graphLine?.clear();
        this.graphLine
            .lineStyle(_thickness, 0xcf1426, 1)
            .moveTo(0, _h)
            .lineTo(0, _h)
            .quadraticCurveTo(
                _w / 2, _h,
                _w, 0,
            )
        this.graphLine.endFill();

        this.graphFill.pivot.y = _h;
        this.graphLine.pivot.y = _h;
    }


    createPlane()
    {
        this.plane = new PIXI.AnimatedSprite([
            PIXI.Resources.textures["plane_0"],
            PIXI.Resources.textures["plane_1"],
            PIXI.Resources.textures["plane_2"],
            PIXI.Resources.textures["plane_1"],
            PIXI.Resources.textures["plane_0"]
        ]);
        this.plane.loop = true;
        this.plane.animationSpeed = .167;

        this.plane.play();
        this.plane.anchor.set(.2, .83);
        this.containerCanvas.addChild(this.plane);
    }


    onResize()
    {
        this.plane.scale.set(
            .65 + Math.min(1, Math.max(0, (this.app.width - 600) / 300)) * .35
        );
        
        this.points = [
            new PIXI.Point(
                0,
                this.app.height
            ),
            new PIXI.Point(
                (this.app.width - this.pad) * ((1387 - this.pad) / (1488 - this.pad)) - (this.plane.width * (1 - this.plane.anchor.x)),
                this.app.height + ((this.plane.height * this.plane.anchor.y) - (this.app.height - this.pad) * ((800 - this.pad) / (808 - this.pad)))
            ),
            new PIXI.Point(
                (this.app.width - this.pad) * ((1462 - this.pad) / (1488 - this.pad)) - (this.plane.width * (1 - this.plane.anchor.x)),
                this.app.height + ((this.plane.height * this.plane.anchor.y) - (this.app.height - this.pad) * ((652 - this.pad) / (808 - this.pad)))
            ),
        ];

        this.createGraph();
        this.graphContainer.y = this.app.height;

        this.graphMask.position.set(this.points[0].x, this.points[0].y + 5);
    }


    reset()
    {
        gsap.killTweensOf(this.plane);
        this.plane.position.set(
            this.points[0].x,
            this.points[0].y
        );
        this.plane.visible = false;
        this.graphMask.width = 0;
        this.graphMask.height = 0;
        this.graphFill.scale.set(1);
        this.graphLine.scale.set(1);
        this.gameOver = true;
    }


    tickerUpdate()
    {
        if (this.gameOver)
        {
            requestAnimationFrame(this.tickerUpdate);

            return;
        }

        let time = performance.now() - this.roundStartedAt;

        let x: number;
        let y: number;

        if (time < 8000)
        {
            const p = Power1.easeOut(time / 8000);

            const p1 = this.points[0];
            const p2 = this.points[1];

            x = p1.x + (p2.x - p1.x) * p;
            y = p1.y + (p2.y - p1.y) * Power1.easeIn(p);

            this.graphMask.width = (p2.x - p1.x) * p;
            this.graphMask.height = (p2.y - p1.y) * Power1.easeIn(p) - this.graphLineThickness;
        }
        else
        {
            this.graphMask.width = this.app.width;
            this.graphMask.height = -this.app.height - 5;
            const idx = Math.floor((time - 8000) / 4000) % 2;
            
            const p1 = this.points[1 + idx % 2];
            const p2 = this.points[1 + (idx + 1) % 2];

            const p = Power1.easeInOut(((time - 8000) % 4000) / 4000);

            x = p1.x + (p2.x - p1.x) * p;
            y = p1.y + (p2.y - p1.y) * p;

            const sx = x / this.points[1].x;
            const sy = (this.app.height - y) / (this.app.height - this.points[1].y);

            this.graphFill.scale.set(sx, sy);
            this.graphLine.scale.set(sx, sy);
            
        }

        this.plane.position.set(x, y);

        requestAnimationFrame(this.tickerUpdate);
    }


}

export namespace PlaneAndGraph
{
    export interface Props
    {
    }
}